// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Loader2, Users, CheckCircle, Clock, XCircle, DollarSign, ChevronDown, ChevronRight } from "lucide-react";
import { useUpcomingEventsForScorekeepers, useScorekeepersEventSummary, useScorekeeperAssignments } from "@/hooks/useScorekeepersData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const statusColors: Record<string, string> = {
  invited: "bg-yellow-500 text-white",
  confirmed: "bg-blue-500 text-white",
  declined: "bg-red-500 text-white",
  checked_in: "bg-green-500 text-white",
  completed: "bg-purple-500 text-white",
  paid: "bg-green-600 text-white",
  no_show: "bg-red-600 text-white",
};

const SKEventCoverageTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForScorekeepers();
  const queryClient = useQueryClient();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: summary, isLoading: sumLoading } = useScorekeepersEventSummary(selectedEventId);
  const { data: assignments } = useScorekeeperAssignments(selectedEventId);
  const [openVenues, setOpenVenues] = useState<Record<string, boolean>>({});

  const venueGroups = useMemo(() => {
    const groups: Record<string, { venue_name: string; rows: any[] }> = {};
    (summary || []).forEach((s: any) => {
      const key = s.venue_id;
      if (!groups[key]) groups[key] = { venue_name: s.venue_name || key, rows: [] };
      groups[key].rows.push(s);
    });
    Object.values(groups).forEach(g => g.rows.sort((a: any, b: any) => (a.assignment_date || "").localeCompare(b.assignment_date || "")));
    return groups;
  }, [summary]);

  const totals = useMemo(() => {
    let assigned = 0, confirmed = 0, awaiting = 0, declined = 0, pay = 0;
    (summary || []).forEach((s: any) => {
      assigned += Number(s.total_assigned) || 0;
      confirmed += Number(s.confirmed) || 0;
      awaiting += Number(s.awaiting_response) || 0;
      declined += Number(s.declined) || 0;
      pay += Number(s.total_pay) || 0;
    });
    return { assigned, confirmed, awaiting, declined, pay };
  }, [summary]);

  const handleStatusChange = async (id: string, status: string) => {
    const { error } = await supabase.from("manage_scorekeeper_assignments").update({ status }).eq("id", id);
    if (error) { toast.error("Failed to update status"); return; }
    toast.success(`Status updated to ${status}`);
    queryClient.invalidateQueries({ queryKey: ["scorekeeper-assignments", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["scorekeepers-event-summary", selectedEventId] });
  };

  const getRowColor = (row: any) => {
    if (Number(row.confirmed) === Number(row.total_assigned) && Number(row.total_assigned) > 0) return "bg-green-50/50";
    if (Number(row.declined) > 0) return "bg-red-50/50";
    if (Number(row.awaiting_response) > 0) return "bg-yellow-50/50";
    return "";
  };

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="min-w-[280px]">
        <Label>Select Event</Label>
        <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
          <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
          <SelectContent>
            {(events || []).map((e: any) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name} ({format(parseLocalDate(e.start_date), "MMM d")} - {format(parseLocalDate(e.end_date), "MMM d, yyyy")})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEventId && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              <div><p className="text-xl font-bold">{totals.assigned}</p><p className="text-xs text-muted-foreground">Total Assigned</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-blue-500" />
              <div><p className="text-xl font-bold">{totals.confirmed}</p><p className="text-xs text-muted-foreground">Confirmed</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Clock className="h-6 w-6 text-yellow-500" />
              <div><p className="text-xl font-bold">{totals.awaiting}</p><p className="text-xs text-muted-foreground">Awaiting</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <XCircle className="h-6 w-6 text-red-500" />
              <div><p className="text-xl font-bold">{totals.declined}</p><p className="text-xs text-muted-foreground">Declined</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-secondary" />
              <div><p className="text-xl font-bold">${totals.pay.toLocaleString()}</p><p className="text-xs text-muted-foreground">Est. Pay</p></div>
            </CardContent></Card>
          </div>

          {sumLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : Object.keys(venueGroups).length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No scorekeeper assignments found for this event.</CardContent></Card>
          ) : (
            Object.entries(venueGroups).map(([venueId, group]) => {
              const isOpen = openVenues[venueId] ?? false;
              const venueAssignments = (assignments || []).filter((a: any) => a.venue_id === venueId);
              return (
                <Card key={venueId}>
                  <Collapsible open={isOpen} onOpenChange={(v) => setOpenVenues(prev => ({ ...prev, [venueId]: v }))}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        {group.venue_name}
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</Button>
                        </CollapsibleTrigger>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead className="text-right">Assigned</TableHead>
                              <TableHead className="text-right">Confirmed</TableHead>
                              <TableHead className="text-right">Awaiting</TableHead>
                              <TableHead className="text-right">Declined</TableHead>
                              <TableHead className="text-right">Games</TableHead>
                              <TableHead className="text-right">Est. Pay</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.rows.map((r: any, i: number) => (
                              <TableRow key={i} className={getRowColor(r)}>
                                <TableCell className="font-medium">{r.assignment_date ? format(new Date(r.assignment_date), "EEE, MMM d") : "—"}</TableCell>
                                <TableCell className="text-right">{Number(r.total_assigned) || 0}</TableCell>
                                <TableCell className="text-right">{Number(r.confirmed) || 0}</TableCell>
                                <TableCell className="text-right">{Number(r.awaiting_response) || 0}</TableCell>
                                <TableCell className="text-right">{Number(r.declined) || 0}</TableCell>
                                <TableCell className="text-right">{Number(r.total_games) || 0}</TableCell>
                                <TableCell className="text-right">${(Number(r.total_pay) || 0).toLocaleString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <CollapsibleContent>
                        {venueAssignments.length > 0 && (
                          <div className="mt-3 border-t pt-3 overflow-x-auto">
                            <p className="text-sm font-medium text-muted-foreground mb-2">Assignment Details</p>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Scorekeeper</TableHead>
                                  <TableHead>Phone</TableHead>
                                  <TableHead>Court</TableHead>
                                  <TableHead>Shift</TableHead>
                                  <TableHead className="text-right">Games</TableHead>
                                  <TableHead className="text-right">Pay</TableHead>
                                  <TableHead>Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {venueAssignments.map((a: any) => (
                                  <TableRow key={a.id}>
                                    <TableCell className="font-medium">{a.scorekeeper?.name || "—"}</TableCell>
                                    <TableCell className="text-sm">{a.scorekeeper?.phone || "—"}</TableCell>
                                    <TableCell className="text-sm">{a.court_id || "—"}</TableCell>
                                    <TableCell className="text-sm">{a.shift_start && a.shift_end ? `${a.shift_start}–${a.shift_end}` : "—"}</TableCell>
                                    <TableCell className="text-right">{a.game_count || "—"}</TableCell>
                                    <TableCell className="text-right">{a.pay_amount ? `$${Number(a.pay_amount).toLocaleString()}` : "—"}</TableCell>
                                    <TableCell>
                                      <Select value={a.status} onValueChange={(v) => handleStatusChange(a.id, v)}>
                                        <SelectTrigger className="w-[130px]">
                                          <Badge className={statusColors[a.status] || "bg-gray-400 text-white"}>
                                            {a.status === "paid" ? "✓ paid" : a.status?.replace("_", " ")}
                                          </Badge>
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="invited">Invited</SelectItem>
                                          <SelectItem value="confirmed">Confirmed</SelectItem>
                                          <SelectItem value="declined">Declined</SelectItem>
                                          <SelectItem value="checked_in">Checked In</SelectItem>
                                          <SelectItem value="completed">Completed</SelectItem>
                                          <SelectItem value="paid">Paid</SelectItem>
                                          <SelectItem value="no_show">No Show</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </CollapsibleContent>
                    </CardContent>
                  </Collapsible>
                </Card>
              );
            })
          )}
        </>
      )}
    </div>
  );
};

export default SKEventCoverageTab;
